import React from 'react';
import { Badge, Divider } from 'fronton-react';
import { getDomain, patchUrlByLocale } from 'utils';
import {
  PackageIcon,
  CreditCardIcon,
  LayoutIcon,
  UserFocusIcon,
  BookmarkSimpleIcon,
  TicketIcon,
  ArrowUDownLeftIcon,
  ChatTextIcon,
  ShareNetworkIcon,
} from '@fronton/icons-react';
import { useTranslation } from 'react-i18next';
import { useProfileAnalyticsEvent } from 'components/header/hooks';
import { useHideProjects } from 'common/hooks/use-hide-projects';
import { useHidePromoCodes } from 'common/hooks/use-hide-promocodes';
import { UserMenuItem } from '../menu-item';
import { UserMenuProps } from '../user/types';
import { useStoreon } from 'storeon/react';
import { LoyaltyRegistrationBanner } from '../loyalty-registration-banner/loyalty-registration-banner';
import { LoyaltyRegistrationModalStore } from 'reducers';
import {
  sendLoyaltyRegistartionBannerClickEvent,
  sendShowLoyaltyRegistrationModalEvent,
} from 'utils/ga-events';
import { REGISTRATION_MODAL } from 'actions/loyalty-registration-modal';
import { useLoyaltyData } from 'common/hooks/use-loyalty-data';
import { UserMenuLoyaltyLink } from './styles';
import { UserMenuLoyaltyPoints } from './user-menu-loyalty-points';
import { useHideSomeExperiment } from 'common/hooks/use-hide-some-experiment';
import { HIDE_RETURNS_INTERNAL_ID, HIDE_APPEALS_INTERNAL_ID } from 'common/constants';
import { State } from 'store/types';
import { useSiteDomain } from 'components/header/components/searchbar/hooks/use-site-domain';
import { REFERRAL_PROGRAM_INTERNAL_ID } from '../../../../../../common/constants/experiment';

export const UserMenuContent = (props: Omit<UserMenuProps, 'show' | 'onClose'>) => {
  const { shoppingListUrl: shoppingList, shoppingListCount, logout } = props;

  const { t } = useTranslation();
  const { dispatch } = useStoreon<LoyaltyRegistrationModalStore>('loyaltyRegistrationModal');

  const onPurchasesClick = useProfileAnalyticsEvent('purchases');
  const onReturnsClick = useProfileAnalyticsEvent('returns');
  const onLoyaltyClick = useProfileAnalyticsEvent('loyalty');
  const onPromocodesClick = useProfileAnalyticsEvent('promocodes');
  const onProjectsClick = useProfileAnalyticsEvent('projects');
  const onShoppingListClick = useProfileAnalyticsEvent('shoppingList');
  const onAppealsClick = useProfileAnalyticsEvent('appeals');
  const onInfoClick = useProfileAnalyticsEvent('info');
  const onReferralProgramClick = useProfileAnalyticsEvent('referralProgram');
  const { host } = useStoreon<State>('host');
  const domain = getDomain(host ?? '');
  const { isKzDomain } = useSiteDomain(domain);
  const isHideAppeals = useHideSomeExperiment(HIDE_APPEALS_INTERNAL_ID) || isKzDomain;
  const isHideProjects = useHideProjects();
  const isHidePromoCodes = useHidePromoCodes();
  const isHideReturns = useHideSomeExperiment(HIDE_RETURNS_INTERNAL_ID);
  const showReferralProgram = useHideSomeExperiment(REFERRAL_PROGRAM_INTERNAL_ID);
  const {
    fetchedLoyaltyAccountSuccess,
    loyaltyAccountIdentifier,
    points,
    fetchLoyaltyPointsLoading,
    fetchLoyaltyPointsError,
    keycardPro,
  } = useLoyaltyData();
  const showLoyaltyBanner = fetchedLoyaltyAccountSuccess && !loyaltyAccountIdentifier;

  const loyaltyBannerClick = () => {
    sendLoyaltyRegistartionBannerClickEvent();
    sendShowLoyaltyRegistrationModalEvent('header-footer');
    dispatch(REGISTRATION_MODAL.setModalOpenFrom, { from: 'header-footer' });
    dispatch(REGISTRATION_MODAL.resetStore);
    dispatch(REGISTRATION_MODAL.open);
  };

  return (
    <div data-qa="user-menu-list">
      <UserMenuItem
        icon={PackageIcon}
        data-qa="user-purchase-history"
        href={patchUrlByLocale({ url: '/lk/history/' })}
        onClick={onPurchasesClick}
      >
        {t('header.main.user-menu.purchases')}
      </UserMenuItem>
      <UserMenuItem
        icon={showLoyaltyBanner ? undefined : CreditCardIcon}
        data-qa="user-loyalty-program"
        href={showLoyaltyBanner ? undefined : patchUrlByLocale({ url: '/lk/loyalty/' })}
        onClick={showLoyaltyBanner ? loyaltyBannerClick : onLoyaltyClick}
      >
        <UserMenuLoyaltyLink>
          {showLoyaltyBanner ? <LoyaltyRegistrationBanner /> : t('header.main.user-menu.loyal')}
          <UserMenuLoyaltyPoints
            points={points}
            fetchLoyaltyPointsLoading={fetchLoyaltyPointsLoading}
            fetchLoyaltyPointsError={fetchLoyaltyPointsError}
          />
        </UserMenuLoyaltyLink>
      </UserMenuItem>

      {!isHidePromoCodes && (
        <UserMenuItem
          icon={TicketIcon}
          data-qa="user-promocodes"
          href={patchUrlByLocale({ url: '/lk/promocodes/' })}
          onClick={onPromocodesClick}
        >
          {t('header.main.user-menu.promocodes')}
        </UserMenuItem>
      )}

      {showReferralProgram && keycardPro && (
        <UserMenuItem
          icon={ShareNetworkIcon}
          data-qa="user-referral-program"
          href={patchUrlByLocale({ url: '/lk/referral/' })}
          onClick={onReferralProgramClick}
        >
          {t('header.main.user-menu.referral-program')}
        </UserMenuItem>
      )}

      {!isHideProjects && (
        <UserMenuItem
          icon={LayoutIcon}
          data-qa="user-projects"
          href={patchUrlByLocale({ url: '/lk/projects/' })}
          onClick={onProjectsClick}
        >
          {t('header.main.user-menu.projects')}
        </UserMenuItem>
      )}

      <UserMenuItem
        icon={BookmarkSimpleIcon}
        data-qa="user-shopping-list"
        href={patchUrlByLocale({ url: shoppingList })}
        onClick={onShoppingListClick}
      >
        {t('header.main.user-menu.shopping-list')}
        {shoppingListCount > 0 && (
          <Badge data-qa="user-shopping-list-count" size="m" value={shoppingListCount} />
        )}
      </UserMenuItem>
      {!isHideReturns && (
        <UserMenuItem
          icon={ArrowUDownLeftIcon}
          data-qa="user-return-history"
          href={patchUrlByLocale({ url: '/lk/returns/' })}
          onClick={onReturnsClick}
        >
          {t('header.main.user-menu.returns')}
        </UserMenuItem>
      )}
      {!isHideAppeals && (
        <UserMenuItem
          icon={ChatTextIcon}
          data-qa="user-appeals"
          href={patchUrlByLocale({ url: '/lk/appeals/' })}
          onClick={onAppealsClick}
        >
          {t('header.main.user-menu.appeals')}
        </UserMenuItem>
      )}
      <UserMenuItem
        icon={UserFocusIcon}
        data-qa="user-lk-info"
        href={patchUrlByLocale({ url: '/lk/info/' })}
        onClick={onInfoClick}
      >
        {t('header.main.user-menu.private')}
      </UserMenuItem>

      <Divider />

      <UserMenuItem data-qa="user-logout" as="button" onClick={logout}>
        {t('header.main.user-menu.exit')}
      </UserMenuItem>
    </div>
  );
};
